/**
 * v2.97z — Global IMDb Batch Manager.
 *
 * BEFORE: every MovieCarousel fired its own POST /api/imdb-bulk on mount.
 *         A home page with 20+ rails → 20+ separate POSTs on initial paint,
 *         each costing 50-150 ms.
 *
 * AFTER:  callers `register(items)` into a singleton queue. After a 100 ms
 *         debounce window (which collects items from every mounted rail
 *         in the same paint cycle), ONE combined POST is fired. Results
 *         are demultiplexed back to each registrant via their original
 *         (media_type, tmdb_id) key.
 *
 * Backend already accepts arrays — no API change.
 *
 * Usage:
 *   import imdbBatch from "../lib/imdbBatch";
 *   const ratings = await imdbBatch.register(items);
 *   // ratings is the slice for THIS caller, in the same order as `items`.
 */
import { api } from "./api";
import { imdbCache } from "../components/MovieCard";

const DEBOUNCE_MS = 100;
const MAX_BATCH = 200;   // server soft-limit; flush early if we hit it

class ImdbBatchManager {
  constructor() {
    this._queue = [];     // [{ resolve, reject, items, keys }]
    this._timer = null;
  }

  /**
   * Register a list of items for IMDb rating enrichment.
   * Returns a Promise<Array<{imdb_rating, filmistani_score}>> matching
   * `items` index-for-index. Items already in `imdbCache` are skipped
   * silently (returned slot is null — caller doesn't need it anyway).
   */
  register(items) {
    if (!Array.isArray(items) || items.length === 0) {
      return Promise.resolve([]);
    }
    const toFetch = items.filter(
      (it) => it && it.tmdb_id && it.media_type
        && !imdbCache.has(`${it.media_type}-${it.tmdb_id}`)
    );
    if (toFetch.length === 0) {
      return Promise.resolve([]);
    }
    return new Promise((resolve, reject) => {
      this._queue.push({
        resolve,
        reject,
        items: toFetch.map((it) => ({
          media_type: it.media_type,
          tmdb_id: it.tmdb_id,
          vote_average: it.vote_average,
          vote_count: it.vote_count,
          popularity: it.popularity,
          original_language: it.original_language,
          genre_names: it.genre_names,
          release_year: it.release_year,
          release_date: it.release_date || it.first_air_date || null,
        })),
        keys: toFetch.map((it) => `${it.media_type}-${it.tmdb_id}`),
      });
      this._schedule();
    });
  }

  _schedule() {
    if (this._timer) return;
    // Hard-flush early if we've accumulated a lot of items.
    const total = this._queue.reduce((s, q) => s + q.items.length, 0);
    if (total >= MAX_BATCH) {
      this._flush();
      return;
    }
    this._timer = setTimeout(() => this._flush(), DEBOUNCE_MS);
  }

  async _flush() {
    if (this._timer) {
      clearTimeout(this._timer);
      this._timer = null;
    }
    const batch = this._queue;
    this._queue = [];
    if (batch.length === 0) return;

    // Build a combined items list + a key→{rating, filmistani_score} map.
    // Use a Map keyed by `${media_type}-${tmdb_id}` so duplicates across
    // rails collapse to a single network row.
    const combinedMap = new Map();
    for (const q of batch) {
      for (let i = 0; i < q.items.length; i++) {
        const key = q.keys[i];
        if (!combinedMap.has(key)) combinedMap.set(key, q.items[i]);
      }
    }
    const combined = Array.from(combinedMap.values());

    try {
      const r = await api.post("/imdb-bulk", { items: combined });
      const ratings = r.data?.ratings || [];
      // Index ratings by the same (mt, tid) key.
      const ratingByKey = new Map();
      ratings.forEach((d, idx) => {
        const src = combined[idx];
        if (!src) return;
        const key = `${src.media_type}-${src.tmdb_id}`;
        const entry = {
          rating: d.imdb_rating ?? null,
          filmistani_score: d.filmistani_score,
          provisional_score: d.provisional_score ?? false,
          rising_gem: d.rising_gem ?? false,
        };
        ratingByKey.set(key, entry);
        imdbCache.set(key, entry);
      });
      // Notify cards across the page to re-render.
      window.dispatchEvent(new CustomEvent("fm-imdb-cache-update"));
      // Resolve each waiter with its own slice.
      for (const q of batch) {
        q.resolve(q.keys.map((k) => ratingByKey.get(k) || null));
      }
    } catch (e) {
      // Fall through silently — per-card MovieCard.jsx fallback still works.
      for (const q of batch) {
        q.resolve([]); // resolve, don't reject (cards fall back gracefully)
      }
    }
  }
}

const imdbBatch = new ImdbBatchManager();
export default imdbBatch;
